import { defineStore } from 'pinia'
import state from './state'
import getters from './getters'
import usePaginationStore from '../pagination'
import DigestateJobsApiRequest from '@/api/digestateJob'
import { DigestateJob, DigestateJobForm, DigestateJobFormCreate } from '@/models'
import CONSTANTS from '@/utils/constants/string-constants'
import StatusCodeType from '@/enum/StatusCodeType'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { markRaw } from 'vue'
import useGlobalStore from '../global'
import {
  DEFAULT_PAGE_OFFSET,
  DIGESTATE_JOBS_DETAILS_URL_NAME,
  DIGESTATE_JOBS_EDIT_JOB_URL_NAME,
  DIGESTATE_JOBS_LIST_STORE,
  MESSAGE_CONSTANTS,
  Network
} from '@/utils/constants'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListPagingTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import { fetchUntilExhaustion, pluralize } from '@/utils/helpers'
import NotificationType, { NotificationDuration } from '@/enum/NotificationType'
import type { IDigestateJob } from '@/interfaces'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import moment from 'moment'
import ChangeStatus from '@/enum/ChangeStatus'
import { ExpandResponseType } from '@/enum'

const useDigestateJobsStore = defineStore('digestateJobsStore', {
  state: () => state,
  getters,
  actions: {
    async getDigestateJobs(
      storeName: string,
      responseOptions?: Record<string, string | Record<string, string | string[]>>
    ) {
      const listStore = usePaginationStore(storeName)
      const digestateJobApi = new DigestateJobsApiRequest()
      responseOptions = {
        ...responseOptions,
        [ListResponseTypeExpression.Expand]: `${ExpandResponseType.PICKUP_AD_PLANT_SITE},${ExpandResponseType.PICKUP_AD_PLANT_SITE_INFO},${ExpandResponseType.FARM},${ExpandResponseType.DIGESTATE_PRODUCT},
          ${ExpandResponseType.SILO},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.PRICE}`
      }
      listStore.handleListAPICall<DigestateJob>(
        digestateJobApi.getDigestateJobs.bind(digestateJobApi),
        DigestateJob,
        responseOptions
      )
    },

    async getJobsForCalendar(start: string, end: string, options?: any) {
      const digestateJobApi = new DigestateJobsApiRequest()
      const {
        [ListFilterTypeExpression.SearchText]: searchText,
        [ListFilterTypeExpression.Filter]: filterOptions
      } = options ?? {}
      const startDate = moment(start).format('YYYY-MM-DD')
      const endDate = moment(end).format('YYYY-MM-DD')
      let responseOptions: any = {
        [ListResponseTypeExpression.Expand]: `${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.PICKUP_AD_PLANT_SITE},${ExpandResponseType.PRICE}`,
        [ListFilterTypeExpression.Filter]: {
          ...filterOptions,
          [CustomFilterTypeExpression.Expression]: `ScheduleDate>="${startDate}"&&ScheduleDate<="${endDate}"`
        }
      }
      if (searchText) {
        responseOptions = {
          ...responseOptions,
          [ListFilterTypeExpression.SearchText]: searchText
        }
      }
      const result = await fetchUntilExhaustion(
        digestateJobApi.getDigestateJobs.bind(digestateJobApi),
        responseOptions
      )
      if (result.length) {
        const resData = result.map((job) => job).filter((job) => job.scheduleDate)
        return resData
      }
    },

    async getJobDetails(id: string) {
      this.isDigestateFormLoading = true
      try {
        const digestateJobApi = new DigestateJobsApiRequest()
        const result = await digestateJobApi.getDigestateJobById({
          id,
          [ItemTypeExpression.Expand]: `${ExpandResponseType.PICKUP_AD_PLANT_SITE},${ExpandResponseType.PICKUP_AD_PLANT_SITE_INFO},${ExpandResponseType.FARM},${ExpandResponseType.DIGESTATE_PRODUCT},
          ${ExpandResponseType.SILO},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.PRICE}`
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.digestateJobDetails = new DigestateJob(result.data)
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isDigestateFormLoading = false
      }
    },

    async proccedToUpdateJobStatus(id: string, status?: number, currentStatus?: number, statusUpdate?: boolean) {
      if (id) this.isDigestateFormButtonLoading = true
      if (status != undefined) {
        this.digestateJobDetails.status = status
      }
      const result = await this.updateDigestateJobs(id, statusUpdate)
      if (result.status === StatusCodeType.Success && result.data) {
        this.digestateJobDetails = new DigestateJob(result.data)
      } else {
        if (currentStatus != undefined) {
          this.digestateJobDetails.status = currentStatus
        }
      }
      this.isDigestateFormButtonLoading = false
      return result
    },

    resetJobsForm() {
      this.jobForm = new DigestateJobFormCreate()
    },

    async createDigestateJobs(): Promise<{ status: StatusCodeType, data?: DigestateJob }> {
      const digestateJobApi = new DigestateJobsApiRequest()
      if (this.jobForm.scheduleDates == null) {
        this.jobForm.scheduleDates = []
      }
      const digestateForm = markRaw(this.jobForm)
      const globalStore = useGlobalStore()
      const result = await digestateJobApi.createDigestateJob(digestateForm, {
        [ItemTypeExpression.Expand]: `${ExpandResponseType.PICKUP_AD_PLANT_SITE},${ExpandResponseType.PICKUP_AD_PLANT_SITE_INFO},${ExpandResponseType.FARM},${ExpandResponseType.DIGESTATE_PRODUCT},
        ${ExpandResponseType.SILO},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.PRICE}`
      })
      if (result.status === StatusCodeType.Success) {
        globalStore.addToastNotification({
          id: Symbol(`digestate job`),
          message: `Digestate job ${CONSTANTS.CREATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      }
      return result
    },

    async updateDigestateJobs(id: string, isStatusUpdate?: boolean): TApiResponse {
      const digestateJobApi = new DigestateJobsApiRequest()
      const digestateForm = markRaw(new DigestateJobForm(this.digestateJobDetails as IDigestateJob))
      const globalStore = useGlobalStore()
      const result = await digestateJobApi.updateDigestateJob(
        {
          id,
          [ItemTypeExpression.Expand]: `${ExpandResponseType.PICKUP_AD_PLANT_SITE},${ExpandResponseType.PICKUP_AD_PLANT_SITE_INFO},${ExpandResponseType.FARM},${ExpandResponseType.DIGESTATE_PRODUCT},
          ${ExpandResponseType.SILO},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.PRICE}`
        },
        digestateForm
      )
      if (result.status === StatusCodeType.Success) {
        globalStore.addToastNotification({
          id: Symbol(`digestate job`),
          message: `Digestate job ${isStatusUpdate ? CONSTANTS.STATUS_UPDATED_SUCCESSFULLY : CONSTANTS.UPDATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      } else {
        globalStore.addToastNotification({
          id: Symbol(`digestate job`),
          message: `Digestate job ${MESSAGE_CONSTANTS.FAILED_TO_UPDATE}`,
          type: NotificationType.Failed
        })
      }
      return result
    },

    async deleteDigestateJob(id?: string) {
      const digestateJobApi = new DigestateJobsApiRequest()
      const globalStore = useGlobalStore()
      const digestateListStore = usePaginationStore(DIGESTATE_JOBS_LIST_STORE)

      try {
        const templatesToDeleteIds = id ? [id] : (digestateListStore.selectedItems ?? [])
        if (!templatesToDeleteIds.length) throw new Error('No Job to delete')
        let queryParams
        if (
          id ||
          digestateListStore.someItemsSelected ||
          (digestateListStore.allItemsSelected && !digestateListStore.isSelectedAllSnackbarItems)
        ) {
          queryParams = { [DeleteTypeExpression.Expression]: { Id: templatesToDeleteIds } }
        }
        const response = await digestateListStore.handleListDeleteAPICall(
          digestateJobApi.deleteDigestateJob.bind(digestateJobApi),
          queryParams
        )
        if (
          response?.status === StatusCodeType.Success &&
          response.data.succeeded > 0 &&
          response.data.failed === 0
        ) {
          globalStore.addToastNotification({
            id: Symbol(`Job deleted`),
            message: `${pluralize(response.data.succeeded, 'Digestate job')} ${CONSTANTS.DELETED_SUCCESSFULLY}`,
            type: NotificationType.Success
          })
          return Promise.resolve(response.data)
        }
        if (response?.data && response.data.failed > 0) {
          globalStore.handleBulkOperationErrorsView(response, 'Digestate job')
          return Promise.resolve(response.data)
        }
        if (response?.data?.results.length) return Promise.reject(response.data)
        throw new TypeError(Network.TypeError.BulkOperationTypeMismatch)
      } catch (error: any) {
        ErrorHandlerService.handleAndCatchError(error, error)
      }
      return Promise.reject()
    },

    async initializeJobDetailsForm(jobData: any, navigator?: any) {
      const currentRouteName = navigator?.getCurrentRoute()?.name

      if (!navigator || currentRouteName == DIGESTATE_JOBS_DETAILS_URL_NAME) {
        this.digestateJobDetails = new DigestateJob(jobData)
      }
      if (navigator && currentRouteName == DIGESTATE_JOBS_EDIT_JOB_URL_NAME) {
        this.getJobDetails(jobData.id)
      }
    },

    async handleRealtimeJobUpdateOnDetails(jobData: any, navigator: any) {
      const loggedInUserId = localStorage.getItem('userId')
      const currentRouteName = navigator.getCurrentRoute()?.name
      const isUserOnJobDetailsView =
        currentRouteName == DIGESTATE_JOBS_DETAILS_URL_NAME &&
        jobData.id == navigator.getCurrentRoute()?.params?.id
      const isUserOnJobEditView =
        currentRouteName == DIGESTATE_JOBS_EDIT_JOB_URL_NAME &&
        jobData.id == navigator.getCurrentRoute()?.params?.id

      const hasJobDetailsStatusChanged = this.digestateJobDetails.status !== jobData.status
      const hasJobEditDetailsStatusChanged = this.digestateJobDetails.status !== jobData.status
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == jobData.updaterId
      const globalStore = useGlobalStore()
      if (
        !hasLoggedInUserUpdatedTheJob &&
        jobData.changeStatus == ChangeStatus.Deleted &&
        (isUserOnJobDetailsView || isUserOnJobEditView)
      ) {
        globalStore.disableButtonsForRealtime = true
        globalStore.isJobDeletedResponse = true
        return
      }

      if (isUserOnJobDetailsView) {
        if (hasJobDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(jobData, navigator)
        }

        if (!hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeUpdateNotification(jobData, navigator)
        }
      }

      if (isUserOnJobEditView) {
        if (hasJobEditDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobEditDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(jobData, navigator)
        }

        if (!hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeOnEditView()
        }
      }
    },

    async showJobStatusChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_STATUS_CHANGED),
        message: MESSAGE_CONSTANTS.JOB_STATUS_CHANGED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
        }
      })
    },

    async showJobDetailsChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_UPDATED),
        message: MESSAGE_CONSTANTS.JOB_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
        }
      })
    },

    async showJobDetailsChangeOnEditView() {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_EDIT_UPDATED),
        message: MESSAGE_CONSTANTS.JOB_EDIT_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Medium
      })
    },

    async handleRealtimeJobUpdateOnList(job: any) {
      const loggedInUserId = localStorage.getItem('userId')
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == job.updaterId
      if (hasLoggedInUserUpdatedTheJob) return

      const isNewlyAddedJob = job.updatedAt === job.createdAt
      const isUpdatedJob = !isNewlyAddedJob
      const jobsListStore = usePaginationStore(DIGESTATE_JOBS_LIST_STORE)
      const jobIndex = jobsListStore.data?.findIndex((item) => item.id === job.id)
      const hasNoFiltersApplied =
        jobsListStore.offset === DEFAULT_PAGE_OFFSET &&
        Object.entries(jobsListStore.filtersOptions)?.length === 0

      const isJobMatchingFilters = async () => {
        const digestateJobApi = new DigestateJobsApiRequest()
        if (Object.entries(jobsListStore.filtersOptions)?.length > 0) {
          const lastOffset = jobsListStore.offset - jobsListStore.currentPageSize
          const lastLimit = jobsListStore.limit
          const responseOptions = jobsListStore.filtersOptions
          const res = await digestateJobApi.getDigestateJobs({
            ...responseOptions,
            [ListPagingTypeExpression.Offset]: lastOffset,
            [ListPagingTypeExpression.Limit]: lastLimit
          })
          if (res.data.value && res.data.value.length > 0) {
            const jobExists = res.data.value.some((item: IDigestateJob) => item.id === job.id)
            if (jobExists) {
              return true
            }
          }
          return false
        }
        return true
      }

      if (isNewlyAddedJob) {
        if (hasNoFiltersApplied || (await isJobMatchingFilters())) {
          if (jobsListStore.data != null && jobsListStore.data.length != 0) {
            jobsListStore.data?.unshift(new DigestateJob(job))
          } else {
            this.getDigestateJobs(DIGESTATE_JOBS_LIST_STORE)
          }
        }
        return
      }

      if (!isUpdatedJob || !jobsListStore.data || jobIndex === undefined) {
        return
      }

      const shouldRemoveJob =
        job.changeStatus === ChangeStatus.Deleted ||
        (Object.entries(jobsListStore.filtersOptions).length > 0 && !(await isJobMatchingFilters()))

      const shouldAddJob =
        Object.entries(jobsListStore.filtersOptions).length > 0 &&
        (await isJobMatchingFilters()) &&
        jobIndex === -1

      const removeJobNotMatchingFilters = async () => {
        if (jobsListStore.data && jobIndex !== undefined && jobIndex !== -1) {
          jobsListStore.data.splice(jobIndex, 1)
          if (jobsListStore.data.length == 0) {
            jobsListStore.resetPaginationStateAndNotFilters()
            this.getDigestateJobs(DIGESTATE_JOBS_LIST_STORE)
          }
        }
      }

      if (shouldRemoveJob) {
        await removeJobNotMatchingFilters()
        return
      }

      if (shouldAddJob) {
        jobsListStore.data?.unshift(new DigestateJob(job))
        return
      }

      if (jobIndex == -1) return
      jobsListStore.data[jobIndex] = new DigestateJob(job)
    }
  }
})
export default useDigestateJobsStore
